// API Configuration for RPX Loan Pricing Platform

// Priority:
// 1. VITE_API_BASE_URL (from environment variables - can be set in Azure App Service for production)
// 2. In DEV mode: empty string (uses Vite proxy)
// 3. In PROD mode: development API URL
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ||
  (import.meta.env.DEV ? '' : 'https://be-development.creditval.com');

export const apiConfig = {
  baseURL: API_BASE_URL,
  endpoints: {
    // Loan Management
    loans: {
      list: '/api/v1/loans/',
      paginated: '/api/v1/loans/paginated',
      create: '/api/v1/loans/',
      getById: (id: string) => `/api/v1/loans/${id}`,
      update: (id: string) => `/api/v1/loans/${id}`,
      delete: (id: string) => `/api/v1/loans/${id}`,
      getBySystemId: (id: string) => `/api/v1/loans/by-system-id/${id}`,
      editView: (id: string) => `/api/v1/loans/${id}/edit`,
    },
    dashboard: {
      analytics: '/api/v1/dashboard/analytics',
    },
    // Valuation & Pricing
    valuation: {
      loanSummary: '/api/v1/valuation/loan-summary',
      loanPricing: '/api/v1/valuation/loan-pricing',
      loanValuation: '/api/v1/valuation/loan-valuation',
      riskMetrics: '/api/v1/valuation/loan-risk-metrics',
      pricingOutput: '/api/v1/valuation/pricing-output',
      completePricingOutput: '/api/v1/valuation/complete-pricing-output',
      pricingSummary: '/api/v1/valuation/pricing-summary',
    },
    // Launch Configuration (replaces valuation-config)
    launchConfig: {
      current: '/api/v1/launch-config/current',
      valuationDate: '/api/v1/launch-config/valuation-date',
      settlementDate: '/api/v1/launch-config/settlement-date',
      update: '/api/v1/launch-config/',
      updateValuationDate: (date: string) =>
        `/api/v1/launch-config/valuation-date?valuation_date=${date}`,
      updateSettlementDate: (date: string) =>
        `/api/v1/launch-config/settlement-date?settlement_date=${date}`,
    },
    // Export (Enhanced with portfolio analysis)
    exports: {
      loansExcel: '/api/v1/exports/loans/excel',
      pricingResultsExcel: '/api/v1/exports/pricing-results/excel',
      completeReportExcel: '/api/v1/exports/complete-report/excel',
      fairValueReportExcel: '/api/v1/exports/fair-value-report/excel',
      portfolioAnalysisExcel: '/api/v1/exports/portfolio-analysis/excel',
      portfolioSummaryReportExcel:
        '/api/v1/exports/portfolio-summary-report/excel',
      benchmarkRatesExcel: '/api/v1/exports/benchmark-rates/excel',
    },
    // Data Refresh (Enhanced with materialized views)
    refresh: {
      trigger: '/api/v1/valuation/the-refresh',
      materializedViews: '/api/v1/valuation/refresh-materialized-views',
      withCustomDates: (
        valuationDate: string,
        settlementDate: string,
        updateConfig = false
      ) =>
        `/api/v1/valuation/refresh-with-dates?valuation_date=${valuationDate}&settlement_date=${settlementDate}&update_config=${updateConfig}`,
    },
    // User Management
    users: {
      create: '/api/v1/users', // No trailing slash for POST
      invitations: '/api/v1/users/invitations', // No trailing slash
      getAllInvitations: '/api/v1/users/all_invitations',
      getProfile: '/api/v1/users/profile',
      updateProfile: '/api/v1/users/profile',
      getById: (id: string) => `/api/v1/users/${id}`,
      delete: (id: string) => `/api/v1/users/${id}`,
      activate: (id: string) => `/api/v1/users/${id}/activate`,
      resetPassword: (id: string) => `/api/v1/users/${id}/reset-password`,
      resendInvitation: (id: string) => `/api/v1/users/${id}/resend-invitation`,
    },
    // Authentication
    auth: {
      login: '/api/v1/auth/login',
      logout: '/api/v1/auth/logout',
      changePassword: '/api/v1/auth/change-password',
      forgotPassword: '/api/v1/auth/forgot-password',
      resetPassword: '/api/v1/auth/reset-password',
      me: '/api/v1/auth/me',
    },
  },
  // Global axios timeout. Increased so Blue Gamma refresh (with retries) can complete.
  timeout: 600000, // 600 seconds
  headers: {
    'Content-Type': 'application/json',
  },
};
